import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { jsonFetch } from "@/lib/json-fetch";

interface ConstraintItem {
  id: string;
  name: string;
  group: string;
  inherited: boolean;
}

interface AllConstraints {
  groups: Array<{ group: string; items: Array<{ id: string; name: string }> }>;
}

const GROUP_ORDER = ["Domain", "Platform", "Offering", "ICP"] as const;

export function SolutionConstraintsEditor({
  solutionId,
  constraints,
}: {
  solutionId: string;
  constraints: ConstraintItem[];
}) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const togglePicker = (group: string) => {
    setQuery("");
    setPickerOpen((cur) => (cur === group ? null : group));
  };

  // Focus the search box when a group's picker opens.
  useEffect(() => {
    if (pickerOpen) searchRef.current?.focus();
  }, [pickerOpen]);

  const { data: all } = useQuery<AllConstraints>({
    queryKey: ["all-constraints"],
    queryFn: () => jsonFetch<AllConstraints>("/api/dashboard/constraints"),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["solution-detail", solutionId] });
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["dashboard-heatmap"] });
  };

  const add = useMutation({
    mutationFn: async (constraintId: string) => {
      const r = await fetch(`/api/dashboard/solutions/${solutionId}/constraints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ constraintId }),
      });
      if (!r.ok) throw new Error("Add failed");
      return r.json();
    },
    onSuccess: () => {
      setPickerOpen(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (constraintId: string) => {
      const r = await fetch(`/api/dashboard/solutions/${solutionId}/constraints/${constraintId}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error("Remove failed");
      return r.json();
    },
    onSuccess: invalidate,
  });

  const byGroup = new Map<string, ConstraintItem[]>();
  for (const c of constraints) {
    const list = byGroup.get(c.group) ?? [];
    list.push(c);
    byGroup.set(c.group, list);
  }

  return (
    <div className="space-y-2">
      {GROUP_ORDER.map((group) => {
        const items = byGroup.get(group) ?? [];
        const allItems = all?.groups.find((g) => g.group === group)?.items ?? [];
        const existing = new Set(items.map((i) => i.id));
        const available = allItems.filter((i) => !existing.has(i.id));
        const isOpen = pickerOpen === group;
        const q = query.trim().toLowerCase();
        const matches = q ? available.filter((a) => a.name.toLowerCase().includes(q)) : available;

        return (
          <div key={group} className="rounded-md border border-border bg-card p-3">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => togglePicker(group)}
                  className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
                {isOpen && (
                  <div className="absolute right-0 top-full z-20 mt-1 flex max-h-64 w-56 flex-col rounded-md border border-border bg-card shadow-lg">
                    {available.length === 0 ? (
                      <div className="px-3 py-2 text-[11px] text-muted-foreground">
                        All {group} constraints already added.
                      </div>
                    ) : (
                      <>
                        <div className="border-b border-border p-1.5">
                          <input
                            ref={searchRef}
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={`Search ${group}…`}
                            className="h-7 w-full rounded border border-border bg-background px-2 text-[11px] outline-none focus:border-foreground/30"
                          />
                        </div>
                        <div className="overflow-y-auto">
                          {matches.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-muted-foreground">
                              No constraints match.
                            </div>
                          ) : (
                            matches.map((a) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => add.mutate(a.id)}
                                className="block w-full px-3 py-1.5 text-left text-[12px] hover:bg-muted"
                              >
                                {a.name}
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {items.length === 0 && (
                <span className="text-[11px] text-muted-foreground">No constraints set.</span>
              )}
              {items.map((c) => (
                <span
                  key={c.id}
                  className={`group inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    c.inherited ? "bg-muted" : "bg-brand-50"
                  }`}
                  title={c.inherited ? "Inherited from Account" : "Solution-specific"}
                >
                  <span>{c.name}</span>
                  <button
                    type="button"
                    onClick={() => remove.mutate(c.id)}
                    className="rounded text-muted-foreground hover:text-[var(--fit-at-risk)] opacity-50 group-hover:opacity-100"
                    aria-label={`Remove ${c.name}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
